import React, { useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { getAppConfig } from "@config/appConfig";
import { PLACEHOLDER_MOVIE_POSTER } from "@config/ImageSizes";
import toSeoUrl from "@functions/toSeoUrl";
import useViewport from "@hooks/useViewport";
import PlayIcon from "@components/Icons/PlayIcon";
import TrailerButton from "@components/TrailerButton";
import V3SessionButton from "@components/V3/V3SessionButton";
import { getImageAtSize } from "@utils/imageUtils";
import { getSessionType } from "@utils/ticketUtils";
import "./style.scss";

const FilteredExperience = ({ groupedVenues = [], selection = { date: null }, onImageError }) => {
    const config = getAppConfig();
    const fallbackImage = `${config?.cdnUrl}${PLACEHOLDER_MOVIE_POSTER}`;

    const { width } = useViewport();
    const blurbBreakpoint = 640;
    const [activeBlurbs, setActiveBlurbs] = useState({});

    const handleClick = (movieId) => {
        setActiveBlurbs((prev) => ({
            ...prev,
            [movieId]: !prev[movieId],
        }));
    };

    return (
        <div className="experiences__list">
            {groupedVenues.length > 0 && (
                <div className="experiences__groups">
                    {groupedVenues.map((venue, index) => (
                        <div key={`${venue.venue_id}-${index}`} className="experiences__group">
                            <Link
                                className="experiences__venue-name"
                                to={`/venue/${venue.venue_id}/${toSeoUrl(venue.venue_name)}`}
                            >
                                {venue.venue_name} - {venue.venue_distance ?? "?"} km
                            </Link>
                            <address className="experiences__address">
                                {[venue.venue_address, venue.venue_suburb, venue.venue_postcode, venue.venue_state]
                                    .filter(Boolean)
                                    .join(", ")}
                            </address>
                            <div className="experiences__venue-group">
                                {Object.values(venue.sessionsByMovie).map((movie) => {
                                const isActive = activeBlurbs[movie.movie_id];
                                return (
                                    <div key={`${venue.venue_id}-${movie.movie_id}`}  className="experiences__movie-items">
                                        <div className="experiences__movie-item">
                                            <Link
                                                className="experiences__movie-item-link"
                                                to={`/movie/${movie.movie_id}/${toSeoUrl(movie.movie_title)}`}
                                        >
                                                <img
                                                    className="experiences__movie-item-image"
                                                    src={getImageAtSize(movie.movie_image, 280) || fallbackImage}
                                                    alt={movie.movie_title}
                                                    onError={onImageError}
                                            />
                                            </Link>
                                            <div className="experiences__movie-item-content">
                                                <Link
                                                    className="experiences__movie-item-title"
                                                    to={`/movie/${movie.movie_id}/${toSeoUrl(movie.movie_title)}`}
                                            >
                                                    {movie.movie_title}
                                                </Link>

                                                {width >= blurbBreakpoint && (
                                                <button
                                                    className={`experiences__movie-item-description ${isActive ? "active" : ""}`}
                                                    onClick={() => handleClick(movie.movie_id)}
                                                    aria-expanded={isActive}
                                                    aria-label={`${isActive ? "Collapse" : "Expand"} movie description for ${movie.movie_title}`}
                                                >
                                                    {movie.movie_blurb}
                                                </button>
                                            )}
                                                <div className="experiences__meta">
                                                    {movie.movie_rating && (
                                                    <span className="experiences__meta-text">{movie.movie_rating}</span>
                                                )}
                                                    {movie.movie_run_time && (
                                                    <span className="experiences__meta-text">{movie.movie_run_time} mins</span>
                                                )}
                                                    {movie.movie_genre && (
                                                    <span className="experiences__meta-text">{movie.movie_genre}</span>
                                                )}
                                                </div>
                                                {movie.trailer_url && (
                                                <TrailerButton
                                                    extraClass="experiences__movie-item-trailer"
                                                    type="mp4"
                                                    trailerLink={movie.trailer_url}
                                                >
                                                    <PlayIcon className="experiences__movie-item-icon-svg" />
                                                    <span className="experiences__movie-item-trailer-text">Official Trailer</span>
                                                </TrailerButton>
                                            )}
                                            </div>
                                        </div>
                                        <div className="experiences__sessions">
                                            <div className="experiences__date-text">
                                                {selection.date && <>{format(new Date(selection.date), "EEE d MMM")}</>}
                                            </div>
                                            <div className="experiences__movie-item-sessions">
                                                {[...movie.sessions]
                                                    .sort((a, b) => new Date(a.session_date) - new Date(b.session_date))
                                                    .map((session) => {
                                                        const experience_type = getSessionType(session);
                                                        return (
                                                            <V3SessionButton
                                                                key={session.id}
                                                                session={{ ...session, experience_type }}
                                                            />
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                    </div>
                                );
                            })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FilteredExperience;
